import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { SummaryFigures } from "../types";
import { COLOR, fmt } from "../utils/format";

interface CardSpec {
  label: string;
  value: string;
  caption?: string;
  color: string;
  bg: string;
  border: string;
}

/**
 * The 4 compact cards: Carry Forward | Total Income | Total Expenses | Balance
 * on Hand, plus the equation line underneath. 4-across on desktop, 2x2 on
 * tablet, stacked on mobile.
 */
const MonthlySummaryCards: React.FC<{ report: SummaryFigures }> = ({ report }) => {
  const positive = report.balanceOnHand >= 0;

  const cards: CardSpec[] = [
    {
      label: "Carry Forward",
      value: fmt(report.carryForward),
      color: COLOR.slateDark,
      bg: COLOR.slateBg,
      border: "#cfd8dc",
    },
    {
      label: "Total Income",
      value: `+ ${fmt(report.incomeTotal)}`,
      caption: report.depositTotal > 0 ? `incl. ${fmt(report.depositTotal)} partner deposits` : undefined,
      color: COLOR.greenDark,
      bg: COLOR.greenBg,
      border: "#c8e6c9",
    },
    {
      label: "Total Expenses",
      value: `- ${fmt(report.expenseTotal)}`,
      color: COLOR.redDark,
      bg: COLOR.redBg,
      border: "#f5c6c4",
    },
    {
      label: "Balance on Hand",
      value: fmt(report.balanceOnHand),
      color: positive ? COLOR.greenDark : COLOR.redDark,
      bg: positive ? COLOR.greenBg : COLOR.redBg,
      border: positive ? "#c8e6c9" : "#f5c6c4",
    },
  ];

  return (
    <Box sx={{ mb: 3 }} className="bs-summary">
      <Grid container spacing={1.5}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label} sx={{ display: "flex" }}>
            <Box
              className="bs-summary-card"
              sx={{
                width: "100%",
                textAlign: "center",
                py: 1.5,
                px: 2,
                borderRadius: 2,
                backgroundColor: card.bg,
                border: `1px solid ${card.border}`,
              }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: 0.3 }}>
                {card.label}
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: card.color, lineHeight: 1.3 }}>
                {card.value}
              </Typography>
              {card.caption && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {card.caption}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: { xs: "center", md: "left" } }}>
        {fmt(report.carryForward)} + {fmt(report.incomeTotal)} - {fmt(report.expenseTotal)} ={" "}
        <Box component="span" sx={{ fontWeight: 700, color: positive ? COLOR.greenDark : COLOR.redDark }}>
          {fmt(report.balanceOnHand)}
        </Box>
      </Typography>
    </Box>
  );
};

export default MonthlySummaryCards;
